import { useSplitTreatments } from "@splitsoftware/splitio-react";
import { Link } from "@tanstack/react-router";
import { useMemo, useState } from "react";

import GameCategories from "@/components/game-categories";
import GameListImage from "@/components/game-list-image";
import IconStack, { IconsEnum } from "@/components/icon-stack";
import { CouponsIcon } from "@/components/icon-stack/icons";
import PlayedButton from "@/components/played-button";
import PromotedGameTile from "@/components/promoted-game-tile";
import type { EscapeType } from "@/graphql/mutations/use-update-escape";
import { gameUrl } from "@/helpers/games";
import useAnalytics from "@/hooks/use-analytics";
import { useIsBreakPoint } from "@/hooks/use-is-break-point";
import { useTimeout } from "@/hooks/use-timers";
import { cn } from "@/lib/utils";

import Properties from "../../../components/properties";
import useGameStatus from "../../../hooks/use-game-status";
import useSelectedCategories from "../../../hooks/use-selected-categories";
import { FlashIcon, HaloweenIcon } from "../../../icons";
import type { SearchGameType } from "../../../types";
import AvailableTimes from "./available-times";

interface RegularGameTileProps extends React.ComponentProps<"div"> {
  game: SearchGameType;
  searchResultsPosition?: number;
  withoutCompany?: boolean;
  withoutLocation?: boolean;
}

function RegularGameTile({
  game,
  searchResultsPosition = 0,
  withoutCompany = false,
  withoutLocation = false,
  ...props
}: RegularGameTileProps) {
  const [userEscape, setUserEscape] = useState<EscapeType | undefined>(
    undefined
  );

  const { isOpen, nameSuffix } = useGameStatus(game);
  const { primaryCategories } = useSelectedCategories();
  const isDesktop = useIsBreakPoint("md");
  const { treatments } = useSplitTreatments({
    names: ["xmas_bash_2023", "halloween_party"],
  });

  const handlePlayedButtonFinished = () => setUserEscape(undefined);

  const handleGameCompleted = (escapeObj: EscapeType) =>
    setUserEscape(escapeObj);

  const { start, stop } = useTimeout(handlePlayedButtonFinished, 3000);

  const hasCoupons = useMemo(() => !!game.coupons?.length, [game.coupons]);
  const treatmentIcons = useMemo(() => {
    const isXmas =
      treatments.xmas_bash_2023?.treatment === "on" &&
      game.tags.some((tag) => tag.id === "christmas");
    const isHalloween =
      treatments.halloween_party?.treatment === "on" &&
      (game.isScary ||
        game.tags.some((tag) =>
          ["spooky", "scary", "very-scary"].includes(tag.id)
        ));

    const icons: React.ReactNode[] = [];

    if (isXmas) {
      icons.push("⛄");
    }
    if (isHalloween) {
      icons.push(<HaloweenIcon className="-mt-1.5 ml-2.5 inline" />);
    }

    return icons;
  }, [treatments, game]);

  return (
    <div
      className={cn(
        "group relative flex items-center text-cool-gray transition-colors md:items-start md:px-6 md:py-5",
        "hover:md:bg-white/5 hover:after:md:opacity-100",
        'after:absolute after:inset-y-0 after:right-0 after:w-[5px] after:bg-linear-to-l after:from-peach after:to-pale-gold after:opacity-0 after:transition-opacity after:content-[""]',
        isOpen && "text-white"
      )}
      {...props}
    >
      {!!userEscape && (
        // biome-ignore lint/a11y/noStaticElementInteractions: dis fine
        // biome-ignore lint/a11y/noNoninteractiveElementInteractions: legacy
        <div
          className="absolute inset-0 z-100 bg-deep-cove/80"
          onMouseEnter={stop}
          onMouseLeave={() => start()}
        >
          <PlayedButton
            className="size-full md:p-7.5"
            game={{ ...game, myEscape: userEscape }}
            onPlayedButtonFinished={handlePlayedButtonFinished}
          />
        </div>
      )}
      <div className="flex w-full flex-col items-stretch gap-3.5">
        <div className="flex w-full items-center gap-2.5 md:items-start md:gap-5">
          <GameListImage
            className="size-15 md:size-32.5"
            game={game}
            onGameCompleted={handleGameCompleted}
          >
            <div className="absolute -top-0.5 left-1.5 md:left-3">
              <IconStack
                game={game}
                iconClassName="h-6 md:h-8.5 w-4 md:w-5.5"
                icons={[IconsEnum.AWARDS]}
              />
            </div>
          </GameListImage>
          <div className="flex grow-1 flex-col items-stretch gap-0.5 overflow-hidden md:gap-0">
            <div className="flex w-full items-start justify-between gap-2.5">
              <h1 className="font-medium text-13/[1.2em] md:pt-1 md:font-semibold md:text-16/[1.5em]">
                <Link
                  className={cn(
                    "link-overlay",
                    game.hasBookings &&
                      "bg-linear-44 from-5% from-tangerine to-105% to-bright-pink bg-clip-text text-transparent"
                  )}
                  target={isDesktop ? "_blank" : undefined}
                  to={
                    gameUrl(game) +
                    (searchResultsPosition ? `?s=${searchResultsPosition}` : "")
                  }
                >
                  {game.name}
                  {nameSuffix}
                  {game.hasBookings && <FlashIcon className="ml-1 inline" />}
                  {isOpen && hasCoupons && (
                    <CouponsIcon className="ml-2 inline" />
                  )}
                </Link>
                {treatmentIcons}
              </h1>
              <div className="hidden shrink-0 gap-4 md:flex">
                <IconStack
                  game={game}
                  iconClassName="size-[21px]"
                  icons={[IconsEnum.SCORE]}
                />
              </div>
            </div>
            {!withoutCompany && (
              <h2
                className={cn(
                  "pt-1 font-medium text-12/[1.2em] text-cool-gray md:leading-[1.5em]",
                  isOpen && "text-pale-silver"
                )}
              >
                {game.gameLocation.displayName ||
                  game.gameLocation.company.name}
              </h2>
            )}
            <div className="hidden flex-col items-stretch gap-2.5 md:flex md:pt-1.5">
              <p className="line-clamp-2 text-12/[1.5em] text-pale-lavender">
                {game.description}
              </p>
              {game.primaryCategory && (
                <GameCategories
                  categories={[
                    game.primaryCategory,
                    ...game.secondaryCategories,
                  ]}
                  className="gap-1.5"
                  withoutParent={!!primaryCategories.length}
                />
              )}
              <Properties game={game} />
              <AvailableTimes game={game} />
            </div>
            {game.primaryCategory && (
              <GameCategories
                categories={[game.primaryCategory, ...game.secondaryCategories]}
                className="mt-1.5 flex gap-1 md:hidden"
                withoutParent={!!primaryCategories.length}
              />
            )}
          </div>
          <div className="md:hidden">
            <IconStack
              game={game}
              iconClassName="size-[21px]"
              icons={[IconsEnum.SCORE]}
            />
          </div>
        </div>
        <AvailableTimes className="flex md:hidden" game={game} />
      </div>
    </div>
  );
}

function PromotedGameTileWrapper({ game }: RegularGameTileProps) {
  const { track } = useAnalytics();
  const { primaryCategories } = useSelectedCategories();
  const isDesktop = useIsBreakPoint("md");

  const handleClick = () => {
    track("Suggested Game Tapped", {
      // position: number
      company_id: game.gameLocation.company.id,
      company_name: game.gameLocation.company.name,
      game_id: game.id,
      game_name: game.name,
      is_promoted: game.isPromoted,
      location_id: game.gameLocation.id,
    });
  };

  return (
    // biome-ignore lint/a11y/noStaticElementInteractions: dis fine
    // biome-ignore lint/a11y/useKeyWithClickEvents: dis fine
    // biome-ignore lint/a11y/noNoninteractiveElementInteractions: legacy
    <div
      className={cn(
        "group relative flex gap-5 md:px-6 md:py-5",
        "hover:md:bg-white/5 hover:after:md:opacity-100",
        'after:absolute after:inset-y-0 after:right-0 after:w-[5px] after:bg-linear-to-l after:from-peach after:to-pale-gold after:opacity-0 after:transition-opacity after:content-[""]'
      )}
      data-ts-clickable
      data-ts-resolved-bid={game.promotedBidId}
      onClick={handleClick}
    >
      <PromotedGameTile
        className="aspect-[343/168] w-full shrink-0 md:aspect-[343/200] md:h-50 md:w-auto"
        game={{ ...game, location: game.gameLocation }}
      />
      <div className="hidden flex-col items-start gap-2.5 md:flex">
        <p className="line-clamp-5 text-12/[1.5em] text-pale-lavender">
          <Link
            className="link-overlay"
            target={isDesktop ? "_blank" : undefined}
            to={gameUrl(game)}
          >
            {game.description}
          </Link>
        </p>
        {game.primaryCategory && (
          <GameCategories
            categories={[game.primaryCategory, ...game.secondaryCategories]}
            className="gap-1.5"
            withoutParent={!!primaryCategories.length}
          />
        )}
        <Properties game={game} />
      </div>
    </div>
  );
}

export default function GameTime({ game, ...props }: RegularGameTileProps) {
  if (game.isPromoted) {
    return <PromotedGameTileWrapper game={game} {...props} />;
  }

  return <RegularGameTile game={game} {...props} />;
}
